import React from 'react';
import { Collapse, GridLegacy as Grid } from '@mui/material';

import { ExpandMore, ExpandLess, Edit } from '@mui/icons-material';

import { Button } from 'components/Button';

import { SectionGrid } from 'components/BackgroundInformation/styles';

import CustomFieldDisplayValue from 'components/SharedFormComponents/CustomFieldDisplayValue';
import { EmptyValue } from 'components/SharedFormComponents/CustomFieldDisplayValue/styles';

import { AuditStatus, EntityModules, ProtectorType } from 'core/constants';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';

import EntityDropdownDisplayValue from 'components/SharedFormComponents/EntityDropdownDisplayValue';

import {
  getPanelGating,
  INITIAL_RISK_FIELD_KEY,
  RESIDUAL_RISK_FIELD_KEY,
  RiskMatrixValue,
  RiskScoringPanel,
  TARGET_RISK_FIELD_KEY,
} from 'components/RiskMatrix';

import AuditScoreSummary from 'components/Audits/AuditScoreSummary';

import type AuditRecordStore from 'stores/AuditRecord';

import Logger from 'utils/logger';

import { useStore } from 'utils/hooks/store';

import { EntitiesStores } from '../types';
import { canEditEntity } from '../editPermissions';

import { SectionContainer, Section, SectionContainerHeader, Title, TitleWrapper } from './styles';
import EntityRowWrapper from './RowWrapper';
import { getExtraRows, getFieldRenderer, isFieldRequired, isFieldVisible } from './entityFieldConfig';
import EditEntitySection from '../EditEntitySection';

type EntitySectionProps = {
  title: string;
  fieldOrder: string[];
  store: EntitiesStores;
  requiredFieldKeys?: Set<string>;
  sectionKey?: string;
};

const EntitySection: React.FunctionComponent<EntitySectionProps> = ({
  title,
  fieldOrder,
  store,
  requiredFieldKeys,
  sectionKey,
}): React.ReactElement => {
  const entity = store.details?.entity || {};

  const { entityFields } = store.store;
  const { session } = useStore();

  const [keyFieldsExpanded, setKeyFieldsExpanded] = React.useState(true);

  const handleExpandClick = (): void => {
    setKeyFieldsExpanded((prevState) => !prevState);
  };

  const [editing, setEditing] = React.useState(false);

  const fieldValues = Object.fromEntries(fieldOrder.map((key) => [key, entity[key]?.value]));
  const shownFieldOrder = fieldOrder.filter((key) => isFieldVisible(store.entityType, key, fieldValues));

  const { isDrawnByPanel, isPanelAnchor } = getPanelGating(shownFieldOrder);

  const ratingFor = (fieldKey: string): RiskMatrixValue | null =>
    (entity[fieldKey]?.value as unknown as RiskMatrixValue) ?? null;

  const onEdit = (): void => {
    setEditing(true);
  };

  const canEdit = canEditEntity(store.entityType, session?.user, Boolean(store.details?.archivedAt));
  const ExtraRows = getExtraRows(store.entityType);
  const isAudit = store.entityType === EntityModules.Audits;

  return (
    // `section-title` and `edit-entity-button` below repeat once per section, so the section root
    // is the only thing that can scope them.
    <Section $white={isAudit} data-testid={sectionKey ? `entity-section-${sectionKey}` : undefined}>
      <SectionContainerHeader $expanded={keyFieldsExpanded} $white={isAudit}>
        <TitleWrapper onClick={handleExpandClick}>
          {keyFieldsExpanded ? (
            <ExpandLess aria-labelledby="show less" fillOpacity="0.54" />
          ) : (
            <ExpandMore aria-labelledby="show more" fillOpacity="0.54" />
          )}
          <Title id="section-title">{title}</Title>
        </TitleWrapper>
        {!editing && canEdit && (
          <SectionGrid item container xs={2} justifyContent="flex-end">
            <Button onClick={onEdit} id="edit-entity-button">
              <Edit sx={{ width: 20, height: 20, marginRight: '8px' }} />
              Edit
            </Button>
          </SectionGrid>
        )}
      </SectionContainerHeader>
      <Collapse in={keyFieldsExpanded} sx={{ padding: '0 16px' }}>
        {editing && canEdit ? (
          <EditEntitySection
            setEditing={setEditing}
            fieldOrder={fieldOrder}
            store={store}
            requiredFieldKeys={requiredFieldKeys}
          />
        ) : (
          <SectionContainer>
            <Grid container spacing={0}>
              {shownFieldOrder.map((fieldKey, index) => {
                if (isDrawnByPanel(fieldKey)) {
                  // Drawn by the panel beside its inherent sibling.
                  return null;
                }

                if (isPanelAnchor(fieldKey)) {
                  return (
                    <Grid item xs={12} key={fieldKey}>
                      <RiskScoringPanel
                        inherent={ratingFor(INITIAL_RISK_FIELD_KEY)}
                        residual={ratingFor(RESIDUAL_RISK_FIELD_KEY)}
                        target={ratingFor(TARGET_RISK_FIELD_KEY)}
                      />
                    </Grid>
                  );
                }

                let item = entity[fieldKey];

                // BE is not retuning schema for fields
                // FE need to handle this case and take data from /fields/ endpoint
                const entityField = entityFields.getFieldById(fieldKey, item?.value);

                if (!entityField) {
                  return null;
                }

                item = entityField;

                if (!item) {
                  Logger.error('Object is unexpected empty.', { entity, fieldKey, index });
                  return null;
                }

                const isRequired =
                  isFieldRequired(store.entityType, fieldKey) ?? requiredFieldKeys?.has(fieldKey) ?? false;

                if (
                  item.schema.protectorType === ProtectorType.MultiChoice ||
                  item.schema.protectorType === ProtectorType.SingleChoice
                ) {
                  return (
                    <EntityDropdownDisplayValue
                      key={fieldKey}
                      fieldKey={fieldKey}
                      item={item}
                      withDivider={index !== shownFieldOrder.length - 1}
                      required={isRequired}
                      helpText={item.schema.helpText ?? undefined}
                    />
                  );
                }

                // The generic NUMBER renderer would print a bare `87` where the header shows `87%`.
                if (fieldKey === 'audits__score') {
                  const auditScore = (store as AuditRecordStore).details?.score;

                  return (
                    <EntityRowWrapper
                      key={fieldKey}
                      rowName={item.schema.name || 'N/A'}
                      withDivider={index !== shownFieldOrder.length - 1}
                      required={isRequired}
                      helpText={item.schema.helpText ?? undefined}
                    >
                      {auditScore && auditScore.percentage !== null ? (
                        <AuditScoreSummary score={auditScore} data-testid="audit-details-score-summary" />
                      ) : (
                        <EmptyValue id="empty-value">-</EmptyValue>
                      )}
                    </EntityRowWrapper>
                  );
                }

                const render = getFieldRenderer(store.entityType, fieldKey);
                if (render) {
                  return (
                    <EntityRowWrapper
                      key={fieldKey}
                      rowName={item.schema.name || 'N/A'}
                      withDivider={index !== shownFieldOrder.length - 1}
                      required={isRequired}
                      helpText={item.schema.helpText ?? undefined}
                    >
                      {render(item.value)}
                    </EntityRowWrapper>
                  );
                }

                const displayValue =
                  fieldKey === 'events__absenceDuration' && item.value != null
                    ? `${item.value} days`
                    : (item.value as AnswerTypes);

                const notSubmitted =
                  fieldKey === 'audits__dateCompleted' &&
                  item.value == null &&
                  (store as AuditRecordStore).details?.auditStatus !== AuditStatus.Submitted;

                return (
                  <EntityRowWrapper
                    key={fieldKey}
                    rowName={item.schema.name || 'N/A'}
                    withDivider={index !== shownFieldOrder.length - 1}
                    required={isRequired}
                    helpText={item.schema.helpText ?? undefined}
                    fieldKey={fieldKey}
                  >
                    {notSubmitted ? (
                      <EmptyValue id="empty-value">Not submitted</EmptyValue>
                    ) : (
                      <CustomFieldDisplayValue
                        value={displayValue}
                        type={item.schema.protectorType as ProtectorType}
                        schema={item.schema}
                        lookupType={item.schema.lookupType}
                      />
                    )}
                  </EntityRowWrapper>
                );
              })}
              {ExtraRows && <ExtraRows store={store} fieldOrder={shownFieldOrder} />}
            </Grid>
          </SectionContainer>
        )}
      </Collapse>
    </Section>
  );
};

export default EntitySection;
